import Switch from '@mui/material/Switch';
import clsx from 'clsx';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/redux/store';
import { ReactComponent as IdIcon } from '../../icons/Id.svg';
import { CustomTypography } from '../custom/CustomTypography';
import { UploadAndDisplayImage } from '../settings/clientSettings/UploadAndDisplayImage';
import { InputField } from '../shared/InputBlock';
import styles from './ProviderHeader.module.css';

type TProviderHeader = {
  defaultAvatar?: string;
  withPasswordRequired?: boolean;
};

export const ProviderHeader: FC<TProviderHeader> = ({
  defaultAvatar,
  withPasswordRequired = true,
}) => {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const {
    watch,
    formState: { errors },
    control,
  } = useFormContext();
  const client = useSelector((state: RootState) => state.app.clientProfile);
  const PROJECT_NAME = client?.name || 'PROJECT_NAME';
  const watchDescription = watch('description') || '';

  return (
    <>
      <CustomTypography className={clsx('font-golos', 'text-17-regular', styles.subtitle)}>
        Основная информация
      </CustomTypography>
      <InputField
        name={'name'}
        label={'Имя'}
        required={true}
        subtitle="Название, отображаемое пользователям"
      />
      <InputField
        name={'description'}
        label={'Описание'}
        subtitle={`${watchDescription?.length}/255 символов`}
      />
      <div>
        <CustomTypography className={clsx('text-14', styles['input-title'])}>
          Логотип
        </CustomTypography>
        <UploadAndDisplayImage
          componentName="create-provider"
          defaultIcon={defaultAvatar || <IdIcon fill="#CED0D9" className={styles['id-icon']} />}
        />
        {errors.avatar && (
          <CustomTypography color="error" className={clsx('text-14', styles['input-error'])}>
            {errors.avatar.message?.toString()}
          </CustomTypography>
        )}
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Файл формата JPG, PNG, BMP или WEBP. Максимальный размер - 1 МБ.
        </CustomTypography>
      </div>
      <div className={styles.divider} />
      <CustomTypography className={clsx('text-17', styles.subtitle)}>Параметры</CustomTypography>
      {!clientId && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Публичный способ входа
            </CustomTypography>
            <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
              Способ входа будет доступен для добавления в пользовательские приложения
            </CustomTypography>
          </div>
          <Controller
            name="is_public"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                data-id="is-public-switch"
                classes={{ root: styles['switch-root'] }}
                disableRipple
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </div>
      )}
      {withPasswordRequired && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Требовать подтверждение паролем
            </CustomTypography>
            <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
              После авторизации во внешней системе у пользователя будет запрошен пароль от{' '}
              {PROJECT_NAME}
            </CustomTypography>
          </div>
          <Controller
            name="password_required"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                classes={{ root: styles['switch-root'] }}
                disableRipple
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </div>
      )}
    </>
  );
};
